import React from "react";
import {observer} from "mobx-react-lite";
import {Modal, ModalBody, ModalHeader} from "reactstrap";
import ModalDetailContent from "../../molecules/modalDetailContent/modalDetailContent";
import {useTranslation} from "react-i18next";
import ButtonIcon from "../../atoms/button/buttonIcon";
import {IconSource} from "../../../shared/enums/iconSource";
import NavigationButton from "../../atoms/button/navigationButton";
import {MultiVillageHelper} from "../../../shared/multiVillageHelper";
import Occasion from "../../../stores/domainStore/pages/models/occasion";
import "./specialDaysDetailModal.sass";
import {format} from "date-fns-tz";
import {allVillageKeyValue} from "../../../shared/constants";
import HaloIcon from "../../molecules/haloIcon/haloIcon";
import {Color} from "../../../shared/enums/color";
import {MultiVillageSelectObservableList} from "../../../stores/observables/multiVillageSelectObservableList";

/**
 * The interface for Special Days Detail Modal.
 */
export interface SpecialDaysDetailModalProps {
  /**
   * The Special Day detail to be displayed on the modal.
   */
  specialDayDetail: Occasion;
  /**
   * Whether the modal is opened or not. When set to true, the modal will be shown.
   */
  isOpen: boolean;
  /**
   * The handler to handle the toggling event of the modal.
   */
  toggle: () => void;
  /**
   * The handler to handle the edit button clicked event.
   */
  onEditClick: () => void;
}

/**
 * The modal that contains the detail of an occasion.
 */
const SpecialDaysDetailModal: React.FC<SpecialDaysDetailModalProps> = ({
  specialDayDetail,
  isOpen,
  toggle,
  onEditClick
}) => {
  const {t} = useTranslation(["specialDays", "translation"]);

  const villageNames = (villageIds: MultiVillageSelectObservableList) => {
    if (villageIds.selectedItems.length === 0) return allVillageKeyValue.label;

    const villages = villageIds.selectedItems.map((vId) => vId.label);
    return MultiVillageHelper.VillageEllipsis(t, villages, true, 3);
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      modalClassName="special-day-detail-modal"
      centered
      data-testid="specialDayDetailModal"
    >
      <ModalHeader
        toggle={toggle}
        close={<NavigationButton icon={IconSource.exit} onClick={toggle} />}
      >
        <div>
          <HaloIcon iconSource={IconSource.specialDay} haloColor={Color.Dusk} />
          <div>{specialDayDetail.title.text}</div>
        </div>
      </ModalHeader>

      <ModalBody>
        <ModalDetailContent
          icon={IconSource.calendar}
          content={
            specialDayDetail.date.date
              ? format(specialDayDetail.date.date, "dd MMMM yyyy")
              : ""
          }
        />
        <ModalDetailContent
          icon={IconSource.village}
          content={villageNames(specialDayDetail.villageIds)}
          dataTestId="specialDayDetailModal-villages"
        />
        <ModalDetailContent icon={IconSource.meal}>
          <>
            {t("specialDays:labels.catering")}{" "}
            <span className="catering">{`${specialDayDetail.getCateringLabel(
              t
            )}`}</span>
          </>
        </ModalDetailContent>
        <ModalDetailContent
          icon={IconSource.published}
          isCentred={false}
          content={
            specialDayDetail.isPrimary.value
              ? t("specialDays:labels.primary")
              : t("specialDays:labels.secondary")
          }
        />

        <div className="edit-button">
          <ButtonIcon
            type="primary"
            icon={IconSource.edit}
            onClick={onEditClick}
          >
            {t("specialDays:actions.edit")}
          </ButtonIcon>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default observer(SpecialDaysDetailModal);
